import React, { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import * as Icons from "lucide-react";
import { NAV } from "../lib/constants";
import { useAuth } from "../lib/auth";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import AIAssistantDrawer from "./AIAssistantDrawer";
import NotificationBell from "./NotificationBell";
import TourGuide from "./TourGuide";

function Icon({ name, className }) {
  const Cmp = Icons[name] || Icons.Circle;
  return <Cmp className={className} strokeWidth={1.75} />;
}

function Sidebar({ onNavigate }) {
  const { user, logout } = useAuth();
  const initials = (user?.name || "?").split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase();
  return (
    <div className="h-full flex flex-col surface relative" style={{ borderRight: "1px solid var(--border)" }}>
      {/* Ambient background — subtle radial mesh for depth */}
      <div aria-hidden className="absolute inset-0 pointer-events-none" style={{
          background: "radial-gradient(600px 300px at -20% -10%, rgba(99,102,241,0.10), transparent 60%), radial-gradient(400px 300px at 120% 110%, rgba(124,58,237,0.07), transparent 60%)",
          maskImage: "linear-gradient(180deg, #000 60%, transparent)",
        }} />
      {/* Logo */}
      <div className="relative px-5 pt-5 pb-4" style={{ borderBottom: "1px solid var(--border)" }}>
        <div className="flex items-center gap-2.5">
          <div className="relative w-9 h-9 rounded-lg flex items-center justify-center shadow-[0_4px_14px_-4px_rgba(79,70,229,0.5)]" style={{ background: "linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)" }}>
            <Icons.ShieldCheck className="w-5 h-5 text-white" strokeWidth={2} />
          </div>
          <div>
            <div className="font-semibold text-[15px] leading-tight ink tracking-tight">DPDP Control Plane</div>
            <div className="text-[10px] font-medium muted uppercase tracking-widest">Compliance OS</div>
          </div>
        </div>
      </div>

      {/* Nav */}
      <div className="relative flex-1 overflow-y-auto py-3">
        {NAV.map((grp) => (
          <div key={grp.group} className="mb-4 px-3">
            <div className="px-3 pt-2 pb-1.5 label-cap">{grp.group}</div>
            <div className="flex flex-col gap-0.5">
              {grp.items.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  onClick={onNavigate}
                  data-testid={`nav-${item.label.replace(/\s+/g, "-").toLowerCase()}`}
                  className={({ isActive }) =>
                    `group relative flex items-center gap-2.5 px-3 py-2 text-[13px] rounded-lg transition-all duration-200 ${
                      isActive
                        ? "font-semibold nav-active-bar"
                        : "muted hover:ink hover:bg-app-2"
                    }`
                  }
                  style={({ isActive }) => isActive
                    ? { background: "linear-gradient(90deg, rgba(79,70,229,0.10), rgba(79,70,229,0.02))", color: "var(--primary)" }
                    : {}
                  }
                >
                  {({ isActive }) => (
                    <>
                      <Icon name={item.icon} className={`w-4 h-4 transition-transform duration-200 ${isActive ? "scale-110" : "opacity-70 group-hover:opacity-100"}`} />
                      <span className="flex-1 truncate">{item.label}</span>
                    </>
                  )}
                </NavLink>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* User */}
      <div className="relative px-3 py-3" style={{ borderTop: "1px solid var(--border)", background: "linear-gradient(180deg, transparent, rgba(238, 241, 246, 0.4))" }}>
        <div className="flex items-center gap-2.5 px-2 py-1.5 rounded-lg hover:bg-app cursor-default">
          <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-semibold shrink-0 shadow-[0_2px_10px_-2px_rgba(5,150,105,0.45)]" style={{ background: "linear-gradient(135deg, #059669, #10B981)" }}>
            {initials}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[13px] font-medium ink truncate" data-testid="sidebar-user-name">{user?.name}</div>
            <div className="text-[10px] mono uppercase tracking-wider muted">{user?.role?.replace(/_/g, " ")}</div>
          </div>
          <button
            data-testid="sidebar-logout"
            onClick={logout}
            title="Sign out"
            className="p-1.5 rounded-md muted hover:ink hover:bg-app-2 transition-colors"
          >
            <Icons.LogOut className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Layout({ children }) {
  const [open, setOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const location = useLocation();
  const currentSection = NAV.flatMap(g => g.items).find(i => i.path === location.pathname);

  return (
    <div className="min-h-screen bg-app flex">
      <aside className="hidden lg:block w-[264px] shrink-0">
        <div className="fixed inset-y-0 w-[264px]">
          <Sidebar />
        </div>
      </aside>

      <div className="lg:hidden">
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetContent side="left" className="p-0 w-[280px] surface" style={{ borderRight: "1px solid var(--border)" }}>
            <Sidebar onNavigate={() => setOpen(false)} />
          </SheetContent>
        </Sheet>
      </div>

      <div className="flex-1 min-w-0">
        {/* Top bar */}
        <div className="sticky top-0 z-30 glass" style={{ borderBottom: "1px solid var(--border)" }}>
          <div className="flex items-center justify-between px-4 sm:px-6 py-3">
            <div className="flex items-center gap-3">
              <Sheet open={open} onOpenChange={setOpen}>
                <SheetTrigger asChild>
                  <button data-testid="mobile-menu-btn" className="lg:hidden p-2 rounded-lg border hover:bg-app" style={{ borderColor: "var(--border)" }}>
                    <Icons.Menu className="w-4 h-4" />
                  </button>
                </SheetTrigger>
              </Sheet>
              <div className="flex items-center gap-2 text-sm">
                <span className="muted">DPDP Act 2023</span>
                <Icons.ChevronRight className="w-3.5 h-3.5 muted-2" strokeWidth={2.5} />
                <span className="ink font-medium">{currentSection?.label || "Dashboard"}</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <NotificationBell />
              <button
                data-testid="ai-assistant-toggle"
                onClick={() => setAiOpen(true)}
                className="sheen-on-hover flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white rounded-lg transition-all duration-200 hover:shadow-[0_6px_20px_-4px_rgba(79,70,229,0.55)] hover:-translate-y-[1px]"
                style={{ background: "linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)" }}
              >
                <Icons.Sparkles className="w-3.5 h-3.5" />
                <span>Legal AI</span>
              </button>
            </div>
          </div>
        </div>

        <main className="min-h-[calc(100vh-60px)]">
          <div className="px-4 sm:px-6 lg:px-8 py-6 lg:py-8 max-w-[1500px] animate-in">
            {children}
          </div>
        </main>

        <footer className="px-6 lg:px-8 py-6 text-[11px] muted leading-relaxed max-w-4xl" style={{ borderTop: "1px solid var(--border)" }}>
          <strong className="ink font-semibold">Legal Disclaimer.</strong> This platform provides compliance automation and document generation
          support for DPDP readiness. It does not constitute legal advice. Final review should be performed by qualified
          legal counsel or the organization's authorized compliance officer.
        </footer>
      </div>

      <AIAssistantDrawer open={aiOpen} onOpenChange={setAiOpen} />
      <TourGuide />
    </div>
  );
}
